import React, { useState } from 'react'
import { bool, object } from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { CREATE, DELETE, GET_LIST, useClient } from '@peracto/client'
import { Form, Group, HydraTable, Input, TextFilter } from '@peracto/peracto-ui'

import { showApiError } from './apiErrors'

const EMPTY_EXCEPTION = { somoraPartNumber: '', tecDocPartNumber: '' }

const exceptionSchema = yup.object().shape({
    somoraPartNumber: yup.string().trim().required('Enter the Somora part number.'),
    tecDocPartNumber: yup.string().trim().required('Enter the TecDoc part number.'),
})

const RuleExceptions = ({ rule, exceptionsOnly = false }) => {
    const { client } = useClient()
    const [version, setVersion] = useState(0)

    const onFetchData = options => client(GET_LIST, 'tecdoc-part-exceptions', options)

    const onAdd = async (values, actions) => {
        try {
            await client(CREATE, 'tecdoc-part-exceptions', {
                data: {
                    rule: rule['@id'],
                    somoraPartNumber: values.somoraPartNumber.trim(),
                    tecDocPartNumber: values.tecDocPartNumber.trim(),
                },
            })
            toast.success('Exception added.')
            actions.resetForm({ values: EMPTY_EXCEPTION })
            actions.setSubmitting(false)
            setVersion(version + 1)
        } catch (e) {
            showApiError(e, actions)
        }
    }

    const onDelete = async exception => {
        try {
            await client(DELETE, 'tecdoc-part-exceptions', { id: exception['@id'] })
            toast.success(`Exception for ${exception.somoraPartNumber} deleted.`)
            setVersion(version + 1)
        } catch (e) {
            showApiError(e)
        }
    }

    const columns = [
        {
            Header: 'Somora part number',
            accessor: 'somoraPartNumber',
            sortType: 'basic',
            Filter: props => <TextFilter label="Somora part number" {...props} />,
            selectable: false,
        },
        {
            Header: 'TecDoc part number',
            accessor: 'tecDocPartNumber',
            sortType: 'basic',
            Filter: props => <TextFilter label="TecDoc part number" {...props} />,
            selectable: false,
        },
        {
            Header: '',
            id: 'delete',
            disableSortBy: true,
            disableFilters: true,
            selectable: false,
            Cell: ({ row }) => (
                <button
                    type="button"
                    className="btn btn-link text-danger p-0"
                    onClick={() => onDelete(row.original)}
                    aria-label={`Delete the exception for ${row.original.somoraPartNumber}`}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </button>
            ),
        },
    ]

    return (
        <div className="mt-4" id="exceptions">
            <h3>Exceptions</h3>
            <p className="text-muted">
                {exceptionsOnly
                    ? "This brand's parts are only matched through these exceptions."
                    : "An exception gives a part's TecDoc number directly, and overrides this brand's rule for that one part."}
            </p>
            <Form
                values={EMPTY_EXCEPTION}
                schema={exceptionSchema}
                onSubmit={onAdd}
                showActionPanel={false}
                autoComplete="off"
            >
                <Group name="Add an exception" id="add-exception">
                    <Input name="somoraPartNumber" label="Somora part number" required />
                    <Input name="tecDocPartNumber" label="TecDoc part number" required />
                    <button type="submit" className="btn btn-primary">
                        Add Exception
                    </button>
                </Group>
            </Form>
            <div className="card card--transparent">
                <div className="card-body">
                    <HydraTable
                        key={version}
                        columns={columns}
                        onFetchData={onFetchData}
                        alwaysFilter={{ rule: rule['@id'] }}
                    />
                </div>
            </div>
        </div>
    )
}

RuleExceptions.propTypes = {
    rule: object.isRequired,
    exceptionsOnly: bool,
}

export default RuleExceptions
